import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const API = `/api`;

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors duration-300";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch {
      setError("Connexion impossible au serveur");
    }
    setLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Connexion impossible");
      } else {
        const { setToken } = await import("@/adminAuth");
        setToken(data.token);
        navigate("/admin");
      }
    } catch {
      setError("Connexion impossible au serveur");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="w-full max-w-md bg-ink-card border border-white/5 p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo-mark.png" alt="e².law" className="w-9 h-9" />
          <div>
            <p className="font-serif text-xl">Back-office</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">
              Accès réservé au cabinet
            </p>
          </div>
        </div>

        {forgotMode ? (
          <>
            <div className="space-y-7">
              {forgotSent ? (
                <p className="text-sm text-gold leading-relaxed" data-testid="admin-forgot-sent">
                  Si cette adresse correspond à un compte, un email de réinitialisation
                  vient d'être envoyé (valable 1 heure).
                </p>
              ) : (
                <div>
                  <label htmlFor="admin-forgot-email" className="label-accent block mb-3">Votre email</label>
                  <input
                    id="admin-forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="vous@e2.law"
                    className={field}
                    data-testid="admin-forgot-email-input"
                  />
                </div>
              )}
            </div>

            {error && (
              <p className="mt-6 text-xs text-red-400" data-testid="admin-login-error">{error}</p>
            )}

            {!forgotSent && (
              <button
                type="button"
                disabled={loading}
                onClick={submitForgot}
                data-testid="admin-forgot-submit"
                className="mt-10 w-full bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
              >
                {loading ? "Envoi…" : "Recevoir le lien de réinitialisation"}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}
              data-testid="admin-forgot-back"
              className="mt-4 w-full text-xs uppercase tracking-[0.2em] text-[#626773] hover:text-gold transition-colors"
            >
              ← Retour à la connexion
            </button>
          </>
        ) : (
          <>
        <div className="space-y-7">
          <div>
            <label htmlFor="admin-email" className="label-accent block mb-3">Email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@e2.law"
              className={field}
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="label-accent block mb-3">Mot de passe</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className={field}
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="button"
            onClick={() => { setForgotMode(true); setError(""); }}
            data-testid="admin-forgot-link"
            className="text-xs text-[#626773] hover:text-gold transition-colors"
          >
            Mot de passe oublié ?
          </button>
        </div>

        {error && (
          <p className="mt-6 text-xs text-red-400" data-testid="admin-login-error">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-10 w-full bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
        >
          {loading ? "Connexion…" : "Se connecter"}
        </button>
          </>
        )}
      </motion.form>
    </div>
  );
}
